import { extractSectorPaths, type SectorPathData } from './svgParser';
import type { Bounds2D, Point2D, SectorPreviewTransform } from '../models/types';

export type SectorHitEntry = {
  name: string;
  path: string;
  element: SVGGeometryElement;
  bounds: Bounds2D;
};

export type SectorHitMap = {
  sectors: SectorHitEntry[];
  byName: Map<string, SectorHitEntry>;
  destroy: () => void;
};

export const extractPreviewSectors = (schemaSvg?: string | null): SectorPathData[] => {
  if (!schemaSvg || !schemaSvg.trim().startsWith('<')) return [];
  return extractSectorPaths(schemaSvg);
};

const applyTransformAttribute = (
  element: SVGGeometryElement,
  normalizedOffset: Point2D,
  previewTransform: SectorPreviewTransform
) => {
  const transforms = [
    `translate(${normalizedOffset.x + previewTransform.x} ${normalizedOffset.y + previewTransform.y})`,
  ];

  if (previewTransform.scaleX !== 1 || previewTransform.scaleY !== 1) {
    transforms.push(`scale(${previewTransform.scaleX} ${previewTransform.scaleY})`);
  }

  element.setAttribute('transform', transforms.join(' '));
};

export const createSectorHitMap = (
  sectorPaths: SectorPathData[],
  normalizedOffset: Point2D,
  previewTransform: SectorPreviewTransform
): SectorHitMap | null => {
  if (sectorPaths.length === 0) return null;

  const svgNs = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNs, 'svg');
  // Для `isPointInFill` браузеру нужен реальный SVG viewport.
  // Нулевые размеры могут приводить к тому, что hit-test по path всегда возвращает false.
  svg.setAttribute('width', '20000');
  svg.setAttribute('height', '20000');
  svg.setAttribute('viewBox', '0 0 20000 20000');
  svg.style.position = 'absolute';
  svg.style.pointerEvents = 'none';
  svg.style.opacity = '0';
  svg.style.overflow = 'visible';
  svg.style.left = '-99999px';
  svg.style.top = '-99999px';
  document.body.appendChild(svg);

  const sectors: SectorHitEntry[] = [];
  const byName = new Map<string, SectorHitEntry>();

  sectorPaths.forEach(sector => {
    const path = document.createElementNS(svgNs, 'path');
    path.setAttribute('d', sector.path);
    applyTransformAttribute(path, normalizedOffset, previewTransform);
    svg.appendChild(path);

    try {
      const bbox = path.getBBox();
      const entry = {
        name: sector.name,
        path: sector.path,
        element: path,
        bounds: {
          minX: bbox.x,
          minY: bbox.y,
          maxX: bbox.x + bbox.width,
          maxY: bbox.y + bbox.height,
        },
      };
      sectors.push(entry);
      byName.set(entry.name, entry);
    } catch {
      svg.removeChild(path);
    }
  });

  return {
    sectors,
    byName,
    destroy: () => {
      svg.remove();
    },
  };
};

const isPointInsideBounds = (point: Point2D, bounds: Bounds2D) =>
  point.x >= bounds.minX &&
  point.x <= bounds.maxX &&
  point.y >= bounds.minY &&
  point.y <= bounds.maxY;

export const findSectorAtPoint = (
  hitMap: SectorHitMap | null,
  point: Point2D
): SectorHitEntry | null => {
  if (!hitMap) return null;

  const domPoint = new DOMPoint(point.x, point.y);

  for (const sector of hitMap.sectors) {
    if (!isPointInsideBounds(point, sector.bounds)) continue;
    if (sector.element.isPointInFill(domPoint)) return sector;
  }

  return null;
};
